// client/src/components/company/Performance.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { TrendingUp, Search, Download, Info, DollarSign, BarChart2, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { formatInterestRate, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import CompanyAnalytics from './CompanyAnalytics';

// ─── Constants ────────────────────────────────────────────────────────────────

const BENCHMARKS_BY_CURRENCY = {
  EUR: [
    { value: 'ESTR',        label: '€STR'        },
    { value: 'EURIBOR_1M',  label: 'Euribor 1M'  },
    { value: 'EURIBOR_3M',  label: 'Euribor 3M'  },
    { value: 'EURIBOR_6M',  label: 'Euribor 6M'  },
    { value: 'EURIBOR_12M', label: 'Euribor 12M' },
  ],
  GBP: [{ value: 'SONIA', label: 'SONIA' }],
  USD: [{ value: 'SOFR',  label: 'SOFR'  }],
  AUD: [{ value: 'AONIA', label: 'AONIA' }],
};

const DEFAULT_BENCHMARK_BY_CURRENCY = { EUR: 'ESTR', GBP: 'SONIA', USD: 'SOFR', AUD: 'AONIA' };
const CURRENCY_SYMBOLS = { EUR: '€', GBP: '£', USD: '$', AUD: 'A$' };
const YIELD_CURRENCIES = ['EUR', 'GBP', 'USD', 'AUD'];

const COLOR_BARS_ALLOCATION  = '#0ea5e9'; // sky blue
const COLOR_YIELD_ALLOCATION = '#1d4ed8'; // primary navy
const COLOR_BARS_HOT_MONEY   = '#fb8c00'; // dashboard orange (lighter)
const COLOR_YIELD_HOT_MONEY  = '#e65100'; // dashboard orange (dark, matches Hot Money accent elsewhere in the app)
const COLOR_BENCHMARK  = '#4b5563'; // dark grey
// Selector boxes and section rows: same light blue for every product
const THEMES = {
  allocation: { soft: '#e3f2fd', border: '#bbdefb', fieldBg: '#f5f9ff', sectionTint: 'linear-gradient(135deg, rgba(37,99,235,0.07) 0%, rgba(29,78,216,0.04) 100%)' }
};
THEMES.hot_money = THEMES.allocation;
// Hot money: tabs, primary buttons and links of the page turn orange (CSS variables used by App.css)
const HOT_MONEY_CSS_VARS = { '--primary': '#e65100', '--primary-color': '#e65100' };
const GRADIENT_HEADER_ALLOCATION = 'linear-gradient(135deg, var(--primary-color, #2563eb) 0%, var(--primary-dark, #1d4ed8) 100%)';
const GRADIENT_HEADER_HOT_MONEY  = 'linear-gradient(135deg, #fb8c00 0%, #e65100 100%)';
const LEFT_YAXIS_WIDTH  = 60;       // must match the YAxis width prop
const LABEL_COL_WIDTH   = 170;      // px – fixed width for the table's first column
const MONTH_COL_MIN_W   = 90;       // px – minimum width before horizontal scroll kicks in

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const YieldTooltip = ({ active, payload, label, currencySymbol = '€' }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: 'var(--bg-primary, #fff)',
      border: '1px solid var(--border-color, #e0e0e0)',
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary, #1a1a1a)' }}>
        {label}
      </div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, marginBottom: '2px' }}>
          {p.name}:{' '}
          {p.dataKey === 'newAllocations'
            ? p.value != null ? `${currencySymbol} ${Math.round(p.value).toLocaleString('en-US')}` : '—'
            : p.value != null ? `${(p.value * 100).toFixed(3)}%` : '—'
          }
        </div>
      ))}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

// product: se passato (es. 'allocation' dall'Analisi Depositi) blocca lo strumento,
// nasconde il selettore e aggiunge la tab Analytics dello stesso prodotto
const Performance = ({ product }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  // Instrument type (top selector): 'allocation' | 'hot_money'
  const [instrumentType, setInstrumentType] = useState(product || 'allocation');

  // Tab
  const [activeTab, setActiveTab] = useState('yield');

  // ── Tab 1: Interessi Maturati ──
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [exportLoading, setExportLoading]   = useState(false);

  // ── Tab 2: Rendimenti & Allocazioni ──
  const [yieldCurrency, setYieldCurrency] = useState('EUR');
  const [benchmark, setBenchmark]         = useState('ESTR');
  const [yieldData, setYieldData]         = useState([]);
  const [yieldLoading, setYieldLoading]   = useState(false);
  const [yieldError, setYieldError]       = useState('');

  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, text: '' });

  // chart ↔ table alignment: measure label column width after render
  const labelColRef    = useRef(null);
  const [chartLeftMargin, setChartLeftMargin] = useState(50); // initial estimate

  const measureLabelCol = useCallback(() => {
    if (labelColRef.current) {
      const w = labelColRef.current.getBoundingClientRect().width;
      setChartLeftMargin(Math.max(0, Math.round(w) - LEFT_YAXIS_WIDTH));
    }
  }, []);

  useEffect(() => {
    measureLabelCol();
  }, [yieldData, measureLabelCol]);

  // Re-measure on window resize
  useEffect(() => {
    window.addEventListener('resize', measureLabelCol);
    return () => window.removeEventListener('resize', measureLabelCol);
  }, [measureLabelCol]);

  // Close tooltip on outside click
  useEffect(() => {
    const handler = () => setActiveTooltip(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ── Fetch: interessi ──
  useEffect(() => {
    setLoading(true);
    const fetch = async () => {
      try {
        const res = await axios.get('/api/requests/performance', { params: { requestType: instrumentType } });
        setRows(res.data.performance || []);
      } catch {
        setError(t('common.error.generic', { defaultValue: 'Errore nel caricamento dei dati' }));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [instrumentType, t]);

  // Reset benchmark to the currency default when yieldCurrency changes
  useEffect(() => {
    setBenchmark(DEFAULT_BENCHMARK_BY_CURRENCY[yieldCurrency] || 'ESTR');
  }, [yieldCurrency]);

  // ── Fetch: yield ──
  useEffect(() => {
    if (activeTab !== 'yield') return;
    const fetch = async () => {
      setYieldLoading(true);
      setYieldError('');
      try {
        const res = await axios.get('/api/analytics/yield-by-month', { params: { benchmark, currency: yieldCurrency, requestType: instrumentType } });
        setYieldData(res.data.data || []);
      } catch {
        setYieldError(t('common.error.generic', { defaultValue: 'Errore nel caricamento dei dati' }));
      } finally {
        setYieldLoading(false);
      }
    };
    fetch();
  }, [activeTab, benchmark, yieldCurrency, instrumentType, t]);

  // ── Helpers ──
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const formatGrossInterest = (value, currency) => {
    if (value === null || value === undefined) return '—';
    return formatCurrencyWithSymbol(Math.round(value * 100) / 100, currency);
  };

  const availableCurrencies = [...new Set(rows.map(r => r.currency).filter(Boolean))].sort();

  // For the yield tab: only currencies with at least one completed request; EUR always included
  const yieldAvailableCurrencies = YIELD_CURRENCIES.filter(c => c === 'EUR' || availableCurrencies.includes(c));

  const filteredRows = rows.filter(row => {
    if (currencyFilter && row.currency !== currencyFilter) return false;
    if (!row.startDate) return true;
    const start = new Date(row.startDate);
    if (dateFrom && start < new Date(dateFrom)) return false;
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (start > to) return false;
    }
    return true;
  });

  const totals = filteredRows.reduce((acc, row) => {
    if (row.currency && row.grossInterest > 0) {
      acc[row.currency] = (acc[row.currency] || 0) + row.grossInterest;
    }
    return acc;
  }, {});

  const handleExportExcel = async () => {
    if (filteredRows.length === 0) {
      alert(t('requests.noDataToExport', { defaultValue: 'Nessun dato da esportare' }));
      return;
    }
    setExportLoading(true);
    try {
      const params = { requestType: instrumentType };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;
      const response = await axios.get('/api/requests/performance/export/excel', {
        params,
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      const cd = response.headers['content-disposition'];
      let filename = `Performance_${Date.now()}.xlsx`;
      if (cd) { const m = cd.match(/filename="(.+)"/); if (m) filename = m[1]; }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('common.error.exportFailed', { defaultValue: "Errore durante l'esportazione Excel" }));
    } finally {
      setExportLoading(false);
    }
  };

  // ── Y-axis formatters ──
  const currSymbol = CURRENCY_SYMBOLS[yieldCurrency] || yieldCurrency;
  const fmtAllocations = (v) => {
    if (v >= 1_000_000) return `${currSymbol}${(v / 1_000_000).toFixed(0)}M`;
    if (v >= 1_000)     return `${currSymbol}${(v / 1_000).toFixed(0)}K`;
    return `${currSymbol}${v}`;
  };
  const fmtPct = (v) => v != null ? `${(v * 100).toFixed(2)}%` : '';

  const isHotMoney = instrumentType === 'hot_money';
  const colorBars  = isHotMoney ? COLOR_BARS_HOT_MONEY  : COLOR_BARS_ALLOCATION;
  const colorYield = isHotMoney ? COLOR_YIELD_HOT_MONEY : COLOR_YIELD_ALLOCATION;
  const tableHeaderGradient = isHotMoney ? GRADIENT_HEADER_HOT_MONEY : GRADIENT_HEADER_ALLOCATION;
  const theme = isHotMoney ? THEMES.hot_money : THEMES.allocation;
  // Denaro caldo: tassi pagati, non rendimenti
  const yieldTabLabel = isHotMoney
    ? t('performance.tabYieldHotMoney')
    : t('performance.tabYield', { defaultValue: 'Rendimenti & Allocazioni' });

  const instrumentSelector = product ? null : (
    <div className="form-group" style={{ marginBottom: '16px', maxWidth: isMobile ? '100%' : '280px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#424242',
      }}>
        <Filter size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
        {t('performance.instrumentType', { defaultValue: 'Tipo Strumento' })}
      </label>
      <select
        value={instrumentType}
        onChange={e => setInstrumentType(e.target.value)}
        className="form-select-custom"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          border: `1px solid ${theme.border}`,
          borderRadius: '6px',
          backgroundColor: theme.fieldBg,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <option value="allocation">{t('performance.instrumentAllocation', { defaultValue: 'Deposito' })}</option>
        <option value="hot_money">{t('performance.instrumentHotMoney', { defaultValue: 'Hot Money' })}</option>
      </select>
    </div>
  );

  if (loading) {
    return <div className="loading">{t('common.loading', { defaultValue: 'Caricamento...' })}</div>;
  }
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className={`company-dashboard ${isHotMoney ? 'hot-money-accent' : ''}`} style={isHotMoney ? HOT_MONEY_CSS_VARS : undefined}>

      {/* ── Tab navigation (same pattern as PastRequests) ── */}
      <div className="tab-container">
        <div className="tabs">
          <div
            className={`tab ${activeTab === 'yield' ? 'active' : ''}`}
            onClick={() => setActiveTab('yield')}
          >
            {yieldTabLabel}
          </div>
          <div
            className={`tab ${activeTab === 'interests' ? 'active' : ''}`}
            onClick={() => setActiveTab('interests')}
          >
            {t('performance.tabInterests', { defaultValue: 'Interessi Maturati' })}
          </div>
          {product && (
            <div
              className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              {t('performance.tabQuotes')}
            </div>
          )}
        </div>
      </div>

      {/* TAB 3 – Analytics (solo con prodotto fissato) */}
      {product && activeTab === 'analytics' && <CompanyAnalytics product={product} />}

      {/* ════════════════════════════════════════════════════
          TAB 1 – Interessi Maturati
      ════════════════════════════════════════════════════ */}
      {activeTab === 'interests' && (
        <div className="card">
          {/* Header */}
          <div className="card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TrendingUp className="card-title-icon" size={20} />
              <span>{t('performance.title', { defaultValue: 'Performance' })}</span>
            </div>
            {rows.length > 0 && (
              <button
                onClick={handleExportExcel}
                disabled={exportLoading}
                className="btn btn-primary"
                style={{ fontSize: '12px', padding: '2px 14px', height: '28px',
                  display: 'flex', alignItems: 'center', gap: '6px', minWidth: '130px' }}
              >
                {exportLoading ? (
                  <><div className="spinner" style={{ width: '10px', height: '10px' }} />
                    {t('common.exporting', { defaultValue: 'Esportazione...' })}</>
                ) : (
                  <><Download size={12} />{t('common.exportExcel', { defaultValue: 'Export Excel' })}</>
                )}
              </button>
            )}
          </div>

          {instrumentSelector}

          {/* Filters */}
          {rows.length > 0 && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', whiteSpace: 'nowrap' }}>
                  {t('performance.dateFrom', { defaultValue: 'Dal' })}
                </label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid var(--border-color, #e0e0e0)',
                    borderRadius: '6px', background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1a1a1a)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', whiteSpace: 'nowrap' }}>
                  {t('performance.dateTo', { defaultValue: 'Al' })}
                </label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid var(--border-color, #e0e0e0)',
                    borderRadius: '6px', background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1a1a1a)' }} />
              </div>
              {availableCurrencies.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', whiteSpace: 'nowrap' }}>
                    {t('performance.currency', { defaultValue: 'Valuta' })}
                  </label>
                  <select value={currencyFilter} onChange={e => setCurrencyFilter(e.target.value)}
                    style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid var(--border-color, #e0e0e0)',
                      borderRadius: '6px', background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1a1a1a)', cursor: 'pointer' }}>
                    <option value="">{t('performance.allCurrencies', { defaultValue: 'Tutte' })}</option>
                    {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              {(dateFrom || dateTo || currencyFilter) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); setCurrencyFilter(''); }}
                  style={{ fontSize: '11px', padding: '4px 10px', border: '1px solid var(--border-color, #e0e0e0)',
                    borderRadius: '6px', background: 'transparent', color: 'var(--text-secondary, #666)', cursor: 'pointer' }}>
                  {t('common.reset', { defaultValue: 'Reset' })}
                </button>
              )}
            </div>
          )}

          {/* Totals */}
          {Object.keys(totals).length > 0 && (
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {Object.entries(totals).map(([currency, total]) => (
                <div key={currency} style={{ flex: '1', minWidth: '180px',
                  background: 'var(--bg-secondary, #f8f9fa)', border: '1px solid var(--border-color, #e0e0e0)',
                  borderRadius: '8px', padding: '16px 20px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '6px', fontWeight: '500' }}>
                    {t('performance.totalGrossInterest', { defaultValue: 'Interessi Totali' })} ({currency})
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary, #1a1a1a)' }}>
                    {`${Math.round(total).toLocaleString('en-US')} ${currency}`}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table */}
          {filteredRows.length === 0 ? (
            <div className="empty-state">
              <p>{t('performance.noData', { defaultValue: 'Nessuna allocazione completata trovata.' })}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr style={{
                  background: tableHeaderGradient,
                  borderRadius: '8px'
                }}>
                  {[
                    { label: t('performance.requestId',    { defaultValue: 'Richiesta' }) },
                    { label: t('performance.startDate',    { defaultValue: 'Data Inizio' }) },
                    { label: t('performance.endDate',      { defaultValue: 'Data Fine' }) },
                    { label: t('performance.amount',       { defaultValue: 'Importo' }) },
                    { label: t('performance.interestRate', { defaultValue: 'Tasso' }) },
                    { label: '' },
                    { label: t('performance.grossInterest', { defaultValue: 'Interessi' }), infoTooltip: t('performance.tooltipGrossInterest', { defaultValue: 'Valori calcolati con la base di calcolo indicata dalla banca nell\'offerta.' }) },
                  ].map((col, i, arr) => (
                    <th key={i} style={{
                      color: '#ffffff', fontWeight: '600', fontSize: '11px',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      padding: '12px 14px', border: 'none', whiteSpace: 'nowrap',
                      background: i === arr.length - 1 ? 'rgba(255,255,255,0.15)' : 'transparent',
                      width: i === 4 ? '1%' : undefined,
                    }}>
                      {col.infoTooltip ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {col.label}
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              e.nativeEvent.stopImmediatePropagation();
                              if (activeTooltip === 'grossInterest-header') {
                                setActiveTooltip(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPos({ top: rect.top, left: rect.left + rect.width / 2, text: col.infoTooltip });
                                setActiveTooltip('grossInterest-header');
                              }
                            }}
                            style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.75)', display: 'inline-flex', flexShrink: 0 }}
                          >
                            <Info size={11} />
                          </span>
                        </span>
                      ) : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, index) => (
                  <tr key={index}>
                    <td>#{row.requestId}</td>
                    <td>{formatDate(row.startDate)}</td>
                    <td>
                      {row.isOvernight
                        ? t('requests.overnight', { defaultValue: 'O/N' })
                        : formatDate(row.endDate)}
                    </td>
                    <td>{formatCurrencyWithSymbol(row.amount, row.currency)}</td>
                    <td style={{ whiteSpace: 'nowrap', width: '1%' }}>
                      {formatInterestRate(row.interestRate, row.rateType, row.baseRate)}
                    </td>
                    <td style={{ textAlign: 'center', width: '36px' }}>
                      <Link to={`/company/requests/${row.requestMongoId}`}
                        style={{ display: 'inline-flex', color: 'var(--primary-color, #2563eb)' }}>
                        <Search size={14} />
                      </Link>
                    </td>
                    <td style={{
                      fontWeight: '700', fontSize: '15px',
                      color: row.grossInterest > 0
                        ? (isHotMoney ? COLOR_YIELD_HOT_MONEY : 'var(--success-color, #16a34a)')
                        : 'var(--text-secondary, #888)',
                      background: row.grossInterest > 0
                        ? (isHotMoney ? 'rgba(230,81,0,0.07)' : 'rgba(22,163,74,0.07)')
                        : 'transparent',
                    }}>
                      {row.isOvernight
                        ? '—'
                        : row.benchmarkPending
                          ? (
                            <span title={t('performance.benchmarkPendingInfo')} style={{ fontSize: '12px', fontWeight: 500, fontStyle: 'italic' }}>
                              {t('performance.benchmarkPending')}
                            </span>
                          )
                          : formatGrossInterest(row.grossInterest, row.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB 2 – Rendimenti & Allocazioni
      ════════════════════════════════════════════════════ */}
      {activeTab === 'yield' && (
        <div className="card">
          <div className="card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TrendingUp className="card-title-icon" size={20} />
              <span>{yieldTabLabel}</span>
            </div>
          </div>

          {instrumentSelector}

          {/* Currency + Benchmark selectors */}
          <div style={{
            backgroundColor: '#ffffff',
            border: `2px solid ${theme.soft}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '16px',
            }}>
              {/* Currency */}
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                }}>
                  <DollarSign size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('performance.currency', { defaultValue: 'Valuta' })}
                </label>
                <select
                  value={yieldCurrency}
                  onChange={e => setYieldCurrency(e.target.value)}
                  className="form-select-custom"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.fieldBg,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {yieldAvailableCurrencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Benchmark */}
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                }}>
                  <BarChart2 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('performance.benchmark', { defaultValue: 'Benchmark' })}
                </label>
                <select
                  value={benchmark}
                  onChange={e => setBenchmark(e.target.value)}
                  className="form-select-custom"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.fieldBg,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {(BENCHMARKS_BY_CURRENCY[yieldCurrency] || []).map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Chart area */}
          {yieldLoading && (
            <div className="loading">{t('common.loading', { defaultValue: 'Caricamento...' })}</div>
          )}
          {yieldError && (
            <div className="error-message">{yieldError}</div>
          )}
          {!yieldLoading && !yieldError && yieldData.length === 0 && (
            <div className="empty-state">
              <p>{t('performance.noYieldData', { defaultValue: 'Nessun dato disponibile per il periodo selezionato.' })}</p>
            </div>
          )}
          {!yieldLoading && !yieldError && yieldData.length > 0 && (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={yieldData} margin={{ top: 8, right: 40, left: chartLeftMargin, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: 'var(--text-secondary, #666)' }}
                  axisLine={{ stroke: 'var(--border-color, #e0e0e0)' }}
                  tickLine={false}
                />
                {/* Left axis – allocations (€) */}
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tickFormatter={fmtAllocations}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary, #666)' }}
                  axisLine={false}
                  tickLine={false}
                  width={LEFT_YAXIS_WIDTH}
                />
                {/* Right axis – rates (%) */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={fmtPct}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary, #666)' }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<YieldTooltip currencySymbol={currSymbol} />} />
                <Legend
                  wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                  formatter={(value) => (
                    <span style={{ color: 'var(--text-primary, #1a1a1a)' }}>{value}</span>
                  )}
                />
                {/* Bars – new allocations */}
                <Bar
                  yAxisId="left"
                  dataKey="newAllocations"
                  name={t('performance.newAllocations', { defaultValue: `Nuove Allocazioni (${currSymbol})` })}
                  fill={colorBars}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={48}
                  opacity={0.85}
                />
                {/* Line – weighted average rate on new allocations */}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="waRateNewAllocations"
                  name={t('performance.tableWaRateShort', { defaultValue: 'Tasso Medio (%)' })}
                  stroke={colorYield}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: colorYield }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
                {/* Line – benchmark (dashed) */}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="benchmark"
                  name={(BENCHMARKS_BY_CURRENCY[yieldCurrency] || []).find(b => b.value === benchmark)?.label || benchmark}
                  stroke={COLOR_BENCHMARK}
                  strokeWidth={1.8}
                  strokeDasharray="5 4"
                  dot={{ r: 3, fill: COLOR_BENCHMARK, stroke: COLOR_BENCHMARK, strokeWidth: 1, strokeDasharray: '0' }}
                  activeDot={{ r: 5, strokeDasharray: '0' }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}

          {/* ── Data table below chart ── */}
          {!yieldLoading && !yieldError && yieldData.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{
                    background: tableHeaderGradient,
                  }}>
                    <th ref={labelColRef} style={{
                      color: '#fff', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em',
                      textTransform: 'uppercase', padding: '10px 10px', border: 'none',
                      textAlign: 'left', whiteSpace: 'nowrap', width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH,
                      position: 'sticky', left: 0,
                      background: tableHeaderGradient,
                    }} />
                    {yieldData.map(d => (
                      <th key={d.month} style={{
                        color: '#fff', fontWeight: '600', fontSize: '11px',
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                        padding: '10px 14px', border: 'none', textAlign: 'right',
                        whiteSpace: 'nowrap',
                        width: 'auto', minWidth: MONTH_COL_MIN_W, maxWidth: 'none',
                      }}>
                        {d.month}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      isHeader: true,
                      label: t('performance.tableNewAllocations', { defaultValue: 'Nuove Allocazioni' }),
                    },
                    {
                      label: t('performance.tableAmount', { defaultValue: 'Importo' }),
                      key: 'newAllocations',
                      fmt: v => v != null && v > 0 ? `${currSymbol} ${Math.round(v).toLocaleString('en-US')}` : '—',
                      color: colorBars,
                      dataRowIndex: 0,
                    },
                    {
                      label: t('performance.tableWaRateShort', { defaultValue: 'Tasso Medio' }),
                      title: t('performance.tableWaRate', { defaultValue: 'Weighted Average Rate on New Allocations' }),
                      key: 'waRateNewAllocations',
                      fmt: v => v != null ? `${(v * 100).toFixed(3)}%` : '—',
                      color: colorYield,
                      dataRowIndex: 1,
                      infoTooltip: isHotMoney ? t('performance.tooltipTassoMedioHotMoney') : t('performance.tooltipTassoMedio', { defaultValue: 'Totale degli interessi maturati nel mese sulla liquidità investita, includendo sia nuove allocazioni che allocazioni passate in depositi non ancora maturati, pro-rata per i giorni del mese in cui l\'investimento è in essere.' }),
                    },
                    {
                      label: (BENCHMARKS_BY_CURRENCY[yieldCurrency] || []).find(b => b.value === benchmark)?.label || benchmark,
                      key: 'benchmark',
                      fmt: v => v != null ? `${(v * 100).toFixed(3)}%` : '—',
                      color: COLOR_BENCHMARK,
                      dataRowIndex: 2,
                    },
                    /* TEMPORANEAMENTE COMMENTATE
                    {
                      isHeader: true,
                      label: t('performance.tableSectionPerformance', { defaultValue: 'Performance Totale di Periodo' }),
                    },
                    {
                      label: t('performance.tableTotalInterest', { defaultValue: 'Interessi Guadagnati' }),
                      key: 'totalInterest',
                      fmt: v => v != null && v > 0 ? `${currSymbol} ${Math.round(v).toLocaleString('en-US')}` : '—',
                      color: 'var(--success-color, #16a34a)',
                      dataRowIndex: 0,
                    },
                    {
                      label: t('performance.tableYield', { defaultValue: 'Rendimento' }),
                      key: 'yield',
                      fmt: v => v != null ? `${(v * 100).toFixed(3)}%` : '—',
                      color: COLOR_YIELD,
                      dataRowIndex: 1,
                      infoTooltip: t('performance.tooltipRendimento', { defaultValue: 'Rendimento % degli interessi guadagnati sul capitale investito.' }),
                    },
                    */
                  ].map((row, ri) => {
                    if (row.isHeader) {
                      return (
                        <tr key={`section-${ri}`} style={{
                          background: theme.sectionTint,
                        }}>
                          <td style={{
                            padding: '7px 10px', fontWeight: '700', fontSize: '10px',
                            letterSpacing: '0.08em', textTransform: 'uppercase',
                            color: 'var(--text-primary, #1a1a1a)',
                            borderBottom: '1px solid var(--border-color, #e0e0e0)',
                            width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH,
                            position: 'sticky', left: 0,
                            background: theme.sectionTint,
                          }}>
                            {row.label}
                          </td>
                          {yieldData.map(d => (
                            <td key={d.month} style={{ borderBottom: '1px solid var(--border-color, #e0e0e0)' }} />
                          ))}
                        </tr>
                      );
                    }
                    const even = row.dataRowIndex % 2 === 0;
                    return (
                      <tr key={row.key} style={{ background: even ? 'var(--bg-secondary, #f8f9fa)' : 'transparent' }}>
                        <td title={row.title} style={{
                          padding: '9px 10px', fontWeight: '600', fontSize: '11px',
                          color: row.color, whiteSpace: 'nowrap',
                          width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH,
                          borderBottom: '1px solid var(--border-color, #e0e0e0)',
                          position: 'sticky', left: 0,
                          background: even ? 'var(--bg-secondary, #f8f9fa)' : 'var(--bg-primary, #fff)',
                        }}>
                          {row.infoTooltip ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {row.label}
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.nativeEvent.stopImmediatePropagation();
                                  if (activeTooltip === row.key) {
                                    setActiveTooltip(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTooltipPos({ top: rect.top, left: rect.left + rect.width / 2, text: row.infoTooltip });
                                    setActiveTooltip(row.key);
                                  }
                                }}
                                style={{ cursor: 'pointer', color: '#9ca3af', display: 'inline-flex', flexShrink: 0 }}
                              >
                                <Info size={12} />
                              </span>
                            </span>
                          ) : row.label}
                        </td>
                        {yieldData.map(d => (
                          <td key={d.month} style={{
                            padding: '9px 14px', textAlign: 'right',
                            color: 'var(--text-primary, #1a1a1a)',
                            borderBottom: '1px solid var(--border-color, #e0e0e0)',
                            whiteSpace: 'nowrap', maxWidth: 'none',
                          }}>
                            {row.fmt(d[row.key])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* Tooltip via portal — shared across all tabs, rendered on document.body */}
      {activeTooltip && tooltipPos.text && createPortal(
        <div style={{
          position: 'fixed',
          top: tooltipPos.top - 6,
          left: tooltipPos.left,
          transform: 'translate(-50%, -100%)',
          background: '#1f2937',
          color: '#fff',
          borderRadius: '6px',
          padding: '8px 12px',
          fontSize: '11px',
          width: '260px',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          lineHeight: '1.5',
          fontWeight: '400',
          whiteSpace: 'normal',
          pointerEvents: 'none',
        }}>
          {tooltipPos.text}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Performance;
